import constate from 'constate'
import { ComponentProps, useCallback, useEffect, useRef } from 'react'
import { Box, BoxProps } from '~/design_system'
import { useHasChangedOrMounted } from '~/hooks/usePrevious'
import { useMsg } from '~/common/localizationUtils'

// For now we only support links, buttons, checkboxes, and selects
// Inputs, if we need them, will require additional thought since arrow keys would need to be handled differently within them
const INTERACTIVE = ['a[href]', 'button', 'input[type="checkbox"]', 'select']
const cellSelector = (col?: number | string) => {
  const attr = col == null ? '[data-keyboard-grid-cell]' : `[data-keyboard-grid-cell="${col}"]`
  const parts: string[] = []
  for (const s of INTERACTIVE) {
    parts.push(`${s}${attr}`) // the element itself has the attr
    parts.push(`${attr} ${s}`) // or it's a descendant of the cell
  }
  return parts.join(',')
}

// 2D (row + column) grid navigation -- see the `KeyboardAccessibleGrid` component doc comment below for
// the full 1D-vs-2D guidance on when to reach for this vs. `~/hooks/useRovingTabIndex` instead. Only usable
// inside a rendered `<KeyboardAccessibleGrid>`, which supplies the container this hook operates on.
export type KeyboardAccessibleGridContext = ReturnType<typeof useKeyboardAccessibleGrid>
export type KeyboardAccessibleGridProps = {
  activeCell?: { row: number; col: number }
  defaultActiveCell?: { row: number; col: number }
}

export const [_InnerContext, useKeyboardAccessibleGrid] = constate((props: KeyboardAccessibleGridProps) => {
  const msg = useMsg()
  // The container element of the grid
  const containerRef = useRef<HTMLElement | null>(null)
  // The cell that will receive focus when the user tabs into the grid
  const activeCellPositionRef = useRef<{ row: number; col: number }>(
    props.activeCell ?? props.defaultActiveCell ?? { row: 0, col: 0 }
  )

  if (useHasChangedOrMounted(props.activeCell) && props.activeCell) {
    activeCellPositionRef.current = props.activeCell
    fullUpdate()
  }

  // The defaultActiveCell is meant for uncontrolled usage, but we want to allow a basic async loading
  // of that value, so if it starts out as undefined we will honor the first valid value when it comes in.
  const rowHasChanged = useHasChangedOrMounted(props.defaultActiveCell?.row)
  const colHasChanged = useHasChangedOrMounted(props.defaultActiveCell?.col)
  const valIsValid = (val: number | undefined) => val != null && val >= 0
  if (
    (rowHasChanged || colHasChanged) &&
    valIsValid(props.defaultActiveCell?.row) &&
    valIsValid(props.defaultActiveCell?.col)
  ) {
    activeCellPositionRef.current = props.defaultActiveCell!
    fullUpdate()
  }

  function updateInteractiveEls() {
    const containerEl = containerRef.current
    if (!containerEl) return
    const interactiveEls = Array.from(containerEl.querySelectorAll(cellSelector())) as HTMLElement[]
    interactiveEls.forEach(el => {
      const colIndex = el.closest('[data-keyboard-grid-cell]')?.getAttribute('data-keyboard-grid-cell')
      el.setAttribute('data-keyboard-grid-cell-interactive', colIndex ?? 'true')
    })
  }

  function getRows() {
    const el = containerRef.current
    if (!el) return []
    return Array.from(el.querySelectorAll('[data-keyboard-grid-row]')) as HTMLElement[]
  }

  function getAllCells() {
    const el = containerRef.current
    if (!el) return []
    return Array.from(el.querySelectorAll('[data-keyboard-grid-cell]')) as HTMLElement[]
  }

  function getAllInteractiveCells() {
    const el = containerRef.current
    if (!el) return []
    return Array.from(el.querySelectorAll('[data-keyboard-grid-cell-interactive]')) as HTMLElement[]
  }

  function getCell(position: { row: number; col: number }) {
    const rowEl = getRows()[position.row]
    if (!rowEl) return null
    return rowEl.querySelector(`[data-keyboard-grid-cell="${position.col}"]`) as HTMLElement | null
  }

  function getInteractiveCell(position: { row: number; col: number }) {
    const rowEl = getRows()[position.row]
    if (!rowEl) return null
    return rowEl.querySelector(`[data-keyboard-grid-cell-interactive="${position.col}"]`) as HTMLElement | null
  }

  function getActiveCell() {
    return getCell(activeCellPositionRef.current)
  }

  function getActiveCellInteractiveEl() {
    return getInteractiveCell(activeCellPositionRef.current)
  }

  function getHighestColIndex() {
    return getAllCells().reduce((max, cell) => {
      const colIndex = cell.getAttribute('data-keyboard-grid-cell')
      return colIndex ? Math.max(max, parseInt(colIndex)) : max
    }, 0)
  }

  // Helper function to check if a cell is disabled
  function isCellDisabled(cell: HTMLElement | null): boolean {
    if (!cell) return true
    // Check if the cell itself is an interactive element
    if (cell.matches('[data-keyboard-grid-cell-interactive]')) {
      return cell.hasAttribute('disabled')
    }
    // Otherwise, look for an interactive element within the cell
    const interactiveElement = cell.querySelector('[data-keyboard-grid-cell-interactive]') as HTMLElement
    if (!interactiveElement) return false
    return interactiveElement.hasAttribute('disabled')
  }

  // Helper function to check if a row has a checked checkbox
  function isRowSelected(row: HTMLElement): boolean {
    const checkboxContainer = row.querySelector('[data-keyboard-grid-checkbox]')
    if (!checkboxContainer) return false

    // The checkbox might be the container itself or an input inside it
    const checkbox =
      checkboxContainer instanceof HTMLInputElement
        ? checkboxContainer
        : (checkboxContainer.querySelector('input[type="checkbox"]') as HTMLInputElement)

    return checkbox ? checkbox.checked : false
  }

  // UPDATE FUNCTIONS
  // Ran on mount and when the DOM changes

  function updateTabIndexes() {
    getAllInteractiveCells().forEach(interactiveEl => {
      interactiveEl.setAttribute('tabindex', '-1')
    })
    const target = getActiveCellInteractiveEl()
    if (target && !isCellDisabled(target)) {
      target.setAttribute('tabindex', '0')
    }
  }

  function updateContainerA11yAttributes() {
    const el = containerRef.current
    if (!el) return

    const rows = getRows().length
    const maxCols = getHighestColIndex() + 1

    if (rows > 0) el.setAttribute('data-rowcount', String(rows))
    if (maxCols > 0) el.setAttribute('data-colcount', String(maxCols))

    el.setAttribute('role', 'grid')
    el.setAttribute(
      'aria-roledescription',
      msg({ id: 'common.ui.keyboard_grid_roledescription', description: 'item list' })
    )
    el.setAttribute('aria-rowcount', String(rows))
    el.setAttribute('aria-colcount', String(maxCols))
  }

  function updateRowAndCellA11yAttributes() {
    const el = containerRef.current
    if (!el) return

    const rows = el.querySelectorAll('[data-keyboard-grid-row]')

    rows.forEach((row, rowIndex) => {
      row.setAttribute('data-rowindex', String(rowIndex + 1))
      row.setAttribute('aria-selected', String(isRowSelected(row as HTMLElement)))

      row.setAttribute('role', 'row')
      row.setAttribute('aria-rowindex', String(rowIndex + 1))

      const cells = row.querySelectorAll('[data-keyboard-grid-cell]')
      cells.forEach((cell, colIndex) => {
        cell.setAttribute('data-colindex', String(colIndex + 1))
        cell.setAttribute('data-rowindex', String(rowIndex + 1))

        cell.setAttribute('role', 'gridcell')
        cell.setAttribute('aria-colindex', String(colIndex + 1))
        cell.setAttribute(
          'data-active-cell',
          String(rowIndex === activeCellPositionRef.current.row && colIndex === activeCellPositionRef.current.col)
        )
      })
    })
  }

  // FOCUS FUNCTION
  // Ran when the user interacts with the grid

  function focusCell(rowNum: number, colNum: number, direction?: 'up' | 'down' | 'left' | 'right') {
    const totalRows = getRows().length
    const highestColIndex = getHighestColIndex()
    // clamp the row and col to the number of rows and cols and zero on the low end
    const clampedRowNum = Math.max(0, Math.min(rowNum, totalRows - 1))
    const clampedColNum = Math.max(0, Math.min(colNum, highestColIndex))
    const target = getInteractiveCell({ row: clampedRowNum, col: clampedColNum })
    let nextTarget: HTMLElement | null = null

    // If target is disabled, find next enabled cell in the same direction
    if (target && isCellDisabled(target)) {
      if (direction === 'right') {
        for (let col = clampedColNum + 1; col <= highestColIndex; col++) {
          const cell = getInteractiveCell({ row: clampedRowNum, col })
          if (cell && !isCellDisabled(cell)) {
            nextTarget = cell
            activeCellPositionRef.current = { row: clampedRowNum, col }
          }
        }
      } else if (direction === 'left') {
        for (let col = clampedColNum - 1; col >= 0; col--) {
          const cell = getInteractiveCell({ row: clampedRowNum, col })
          if (cell && !isCellDisabled(cell)) {
            nextTarget = cell
            activeCellPositionRef.current = { row: clampedRowNum, col }
          }
        }
      } else if (direction === 'down') {
        for (let row = clampedRowNum + 1; row < totalRows; row++) {
          const cell = getInteractiveCell({ row, col: clampedColNum })
          if (cell && !isCellDisabled(cell)) {
            nextTarget = cell
            activeCellPositionRef.current = { row, col: clampedColNum }
          }
        }
      } else if (direction === 'up') {
        for (let row = clampedRowNum - 1; row >= 0; row--) {
          const cell = getInteractiveCell({ row, col: clampedColNum })
          if (cell && !isCellDisabled(cell)) {
            nextTarget = cell
            activeCellPositionRef.current = { row, col: clampedColNum }
          }
        }
      }

      if (!nextTarget) return
    }

    // Only update active cell and focus if the target is not disabled
    if (target && !isCellDisabled(target)) {
      nextTarget = target
      activeCellPositionRef.current = { row: clampedRowNum, col: clampedColNum }
    }

    // Once we have a target, update the active cell and focus it
    if (nextTarget) {
      updateTabIndexes()
      requestAnimationFrame(() => {
        nextTarget.focus()
        updateRowAndCellA11yAttributes()
      })
    }
  }

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.altKey || e.metaKey) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          e.stopPropagation()
          focusCell(activeCellPositionRef.current.row + 1, activeCellPositionRef.current.col, 'down')
          break
        case 'ArrowUp':
          e.preventDefault()
          e.stopPropagation()
          focusCell(activeCellPositionRef.current.row - 1, activeCellPositionRef.current.col, 'up')
          break
        case 'ArrowRight':
        case 'ArrowLeft': {
          e.preventDefault()
          e.stopPropagation()
          const direction = e.key === 'ArrowRight' ? 'right' : 'left'
          const newCol =
            e.key === 'ArrowRight' ? activeCellPositionRef.current.col + 1 : activeCellPositionRef.current.col - 1
          const highestColIndex = getHighestColIndex()
          if (newCol < 0 && activeCellPositionRef.current.row > 0) {
            focusCell(activeCellPositionRef.current.row - 1, highestColIndex, 'up')
          } else if (newCol > highestColIndex && activeCellPositionRef.current.row < getRows().length - 1) {
            focusCell(activeCellPositionRef.current.row + 1, 0, 'down')
          } else {
            focusCell(activeCellPositionRef.current.row, newCol, direction)
          }
          break
        }
        case 'Home': {
          e.preventDefault()
          e.stopPropagation()
          if (e.ctrlKey) {
            // Move to first cell in the entire grid
            focusCell(0, 0)
          } else {
            // Move to first cell in the current row
            focusCell(activeCellPositionRef.current.row, 0)
          }
          break
        }
        case 'End': {
          e.preventDefault()
          e.stopPropagation()
          if (e.ctrlKey) {
            // Move to last cell in the entire grid
            const lastRowIndex = getRows().length - 1
            const lastColIndex = getHighestColIndex()
            focusCell(lastRowIndex, lastColIndex)
          } else {
            // Move to last cell in the current row
            const lastColIndex = getHighestColIndex()
            focusCell(activeCellPositionRef.current.row, lastColIndex)
          }
          break
        }
        default:
          break
      }
    },
    [focusCell]
  )

  function onClick(e: MouseEvent) {
    const cellEl = e.target instanceof HTMLElement ? e.target.closest('[data-keyboard-grid-cell]') : null
    if (cellEl) {
      const rowIndex = cellEl.getAttribute('data-rowindex')
      const colIndex = cellEl.getAttribute('data-colindex')
      if (rowIndex && colIndex) {
        focusCell(parseInt(rowIndex) - 1, parseInt(colIndex) - 1) // Convert from 1-based to 0-based
      }
      return
    }
    const rowEl = e.target instanceof HTMLElement ? e.target.closest('[data-keyboard-grid-row]') : null
    if (rowEl) {
      const rowIndex = rowEl.getAttribute('data-rowindex')
      if (rowIndex) {
        focusCell(parseInt(rowIndex) - 1, 0) // Convert from 1-based to 0-based
      }
    }
  }

  // Listen for checkbox changes to update aria-selected
  const handleCheckboxChange = (e: Event) => {
    const target = e.target as HTMLElement
    if (target.hasAttribute('data-keyboard-grid-checkbox')) {
      const row = target.closest('[data-keyboard-grid-row]') as HTMLElement
      if (row) {
        row.setAttribute('aria-selected', String(isRowSelected(row)))
      }
    }
  }

  useEffect(() => {
    if (!containerRef.current) throw new Error('KeyboardAccessibleGrid container not found')
    containerRef.current?.addEventListener('keydown', onKeyDown)
    containerRef.current?.addEventListener('click', onClick)
    containerRef.current?.addEventListener('change', handleCheckboxChange)

    return () => {
      containerRef.current?.removeEventListener('keydown', onKeyDown)
      containerRef.current?.removeEventListener('click', onClick)
      containerRef.current?.removeEventListener('change', handleCheckboxChange)
    }
  }, [])

  const observerRef = useRef<MutationObserver | null>(null)
  const rafIdRef = useRef<number | null>(null)
  const resetObserverRefs = () => {
    observerRef.current?.disconnect()
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    observerRef.current = null
    rafIdRef.current = null
  }
  useEffect(() => {
    return () => {
      resetObserverRefs()
    }
  }, [])

  function fullUpdate() {
    updateInteractiveEls()
    updateRowAndCellA11yAttributes()
    updateContainerA11yAttributes()
    updateTabIndexes()
  }

  // Return the CompositeAPI
  return {
    // Imperative API
    getRows,
    getAllCells,
    getAllInteractiveCells,
    getCell,
    getInteractiveCell,
    getActiveCell,
    getHighestColIndex,
    focusCell,

    // Internal API
    _containerRef: (el: HTMLElement | null) => {
      if (el && containerRef.current !== el) {
        resetObserverRefs()
      }

      if (el && !observerRef.current) {
        observerRef.current = new MutationObserver(() => {
          // Debounce to avoid excessive updates
          if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
          rafIdRef.current = requestAnimationFrame(() => {
            fullUpdate()
            rafIdRef.current = null
          })
        })
        observerRef.current.observe(el, {
          childList: true,
          subtree: true
          // Don't observe attribute changes to avoid loops
        })
      }

      // Initial setup when container is set
      if (el && containerRef.current !== el) {
        containerRef.current = el
        fullUpdate()
      }
    }
  }
})

function CompositeContainer({ children, 'aria-label': ariaLabel, ...props }: BoxProps) {
  const composite = useKeyboardAccessibleGrid()
  return (
    <Box ref={composite._containerRef} data-keyboard-grid-container h="full" {...props} aria-label={ariaLabel}>
      {children}
    </Box>
  )
}

/**
 * Two-dimensional (2D) roving-tabindex keyboard navigation for a real row-and-column grid (e.g. a
 * master-detail list with a checkbox column) -- Up/Down move by row (clamped), Left/Right move by column
 * but wrap into the adjacent row (reading-order), and each cell's own native element (button, checkbox,
 * link) handles activation immediately on focus. There's no "focus moves, selection doesn't commit yet"
 * concept here -- moving focus onto a cell is the only kind of "selection" this grid knows about.
 *
 * Use this for actual grids with more than one column. For a single horizontal or vertical line of items
 * -- a toolbar, a tablist, a row of filter tabs -- where arrowing past an item should NOT immediately
 * select it, use `useRovingTabIndex` (`~/hooks/useRovingTabIndex`) instead; it's a much lighter-weight
 * hook built specifically for that "manual activation" (commit only on Enter/Space/click) pattern.
 */
export function KeyboardAccessibleGrid({
  sx,
  children,
  'aria-label': ariaLabel,
  ...props
}: { sx?: BoxProps['sx']; 'aria-label'?: string } & ComponentProps<typeof _InnerContext>) {
  return (
    // eslint-disable-next-line react/jsx-pascal-case
    <_InnerContext {...props}>
      <CompositeContainer aria-label={ariaLabel} sx={sx}>
        {children}
      </CompositeContainer>
    </_InnerContext>
  )
}
